import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export type IValidator<T> = (propName: string, obj: T, translator: TranslateService) => ValidationErrors;

export type ValidatorMap<T> = {
	[K in keyof T]: IValidator<T[K]> | IValidator<T[K]>[];
};

export type ArrayItem<T> = T extends Array<infer U> ? U : never;

export class objectValidator {
	static validateObject<T>(obj: T, validators: ValidatorMap<T>, translator: TranslateService): Record<string, ValidationErrors> {
		const errors: Record<string, ValidationErrors> = {};

		Object.entries(validators).forEach(([property, validators]: [string, IValidator<T> | IValidator<T>[]]) => {
			if (!Array.isArray(validators)) validators = [validators];

			const validationErrors: ValidationErrors = validators.reduce((acc, validator) => {
				const error = validator(property, obj[property], translator);
				if (Object.keys(error).length > 0) {
					return { ...acc, ...error };
				}
				return acc;
			}, {});

			if (Object.keys(validationErrors).length > 0) {
				errors[property] = validationErrors;
			}
		});

		return errors;
	}
}

function isEmptyValue(value: unknown): boolean {
	if (value === undefined || value === null) {
		return true;
	}
	if (Array.isArray(value) || typeof value === 'string') {
		return value.length === 0;
	}
	if (value instanceof Set) {
		return value.size === 0;
	}
	return false;
}

export const ObjectValidators = {
	/** Rejects an empty value, matching Angular's `Validators.required` — so `''` and `[]` fail here, not just `null` / `undefined`. `false` and `0` pass: they are values, not absences. */
	required: <T>(propName: string, prop: T, translator: TranslateService): ValidationErrors => {
		if (isEmptyValue(prop)) {
			return {
				required: translator.instant('modelValidators.errors.required', { propName }),
			};
		}
		return {};
	},
	maxLength<T extends string | number | Array<any>>(maxLength: number): IValidator<T> {
		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			if (typeof prop === 'string' && prop.length > maxLength) {
				return {
					maxLength: translator.instant('modelValidators.errors.max-length', {
						propName,
						maxLength,
					}),
				};
			}
			if (Array.isArray(prop) && prop.length > maxLength) {
				return {
					maxLength: translator.instant('modelValidators.errors.max-items', {
						propName,
						maxLength,
					}),
				};
			}
			return {};
		};
	},
	minLength<T extends string | number | Array<any>>(minLength: number): IValidator<T> {
		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			if (typeof prop === 'string' && prop.length < minLength) {
				return {
					minLength: translator.instant('modelValidators.errors.min-length', {
						propName,
						maxLength: minLength,
					}),
				};
			}
			if (Array.isArray(prop) && prop.length < minLength) {
				return {
					minLength: translator.instant('modelValidators.errors.min-items', {
						propName,
						maxLength: minLength,
					}),
				};
			}
			return {};
		};
	},
	min(minValue: number): IValidator<number> {
		return (propName: string, prop: number, translator: TranslateService): ValidationErrors => {
			if (typeof prop === 'number' && prop < minValue) {
				return {
					min: translator.instant('modelValidators.errors.min', { propName, minValue }),
				};
			}
			return {};
		};
	},
	max(maxValue: number): IValidator<number> {
		return (propName: string, prop: number, translator: TranslateService): ValidationErrors => {
			if (typeof prop === 'number' && prop > maxValue) {
				return {
					max: translator.instant('modelValidators.errors.max', { propName, maxValue }),
				};
			}
			return {};
		};
	},
	integer: (propName: string, prop: number, translator: TranslateService): ValidationErrors => {
		if (typeof prop === 'number' && !Number.isInteger(prop)) {
			return { integer: translator.instant('modelValidators.errors.integer', { propName }) };
		}
		return {};
	},
	pattern<T extends string>(pattern: RegExp | string, errorMsg?: string, addPropName?: boolean): IValidator<T> {
		let regex: RegExp;
		if (typeof pattern === 'string') {
			regex = new RegExp(pattern);
		} else {
			regex = pattern;
		}

		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			// Emptiness is `required`'s job — a field that must be present declares both.
			if (isEmptyValue(prop)) {
				return {};
			}

			if (typeof prop === 'string' && !regex.test(prop)) {
				const message = translator.instant(errorMsg ?? 'modelValidators.errors.pattern', {
					propName,
				});
				if (addPropName) return { pattern: `${propName}: ${message}` };

				return { pattern: message };
			}
			return {};
		};
	},
	notEmpty: <T extends Array<any>>(propName: string, prop: T, translator: TranslateService): ValidationErrors => {
		if (!prop || (Array.isArray(prop) && prop.length === 0)) {
			return {
				notEmpty: translator.instant('modelValidators.errors.not-empty', { propName }),
			};
		}
		return {};
	},
	/**
	 * Requires the value to be an array.
	 *
	 * **Not redundant with `array` below,** which returns no errors at all for a non-array — so without this check a property holding `{}` (or `42`, or a string) validates clean and reaches the API. That matters wherever a value arrives from parsed JSON rather than a typed form.
	 *
	 * Absence is left to `required` so each check reports one thing.
	 */
	isArray<T>(errorMsg?: string): IValidator<T> {
		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			if (prop === undefined || prop === null || Array.isArray(prop)) {
				return {};
			}

			return {
				notAList: translator.instant(errorMsg ?? 'modelValidators.errors.not-an-array', {
					propName,
				}),
			};
		};
	},
	/**
	 * Requires the value to be a member of `allowed`.
	 *
	 * For properties that are enums on the wire rather than free strings. A typed form can only offer the fixed list, so an out-of-range value generally means the data came from an import or an API. Absence is left to `required` so each check reports one thing.
	 */
	oneOf<T extends string>(allowed: readonly T[], errorMsg?: string): IValidator<T> {
		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			if (prop === undefined || prop === null) {
				return {};
			}

			if (typeof prop !== 'string' || !allowed.includes(prop)) {
				return {
					oneOf: translator.instant(errorMsg ?? 'modelValidators.errors.one-of', {
						propName,
						value: prop,
						validValues: allowed.join(', '),
					}),
				};
			}

			return {};
		};
	},
	array<T extends Array<any>>(validators: IValidator<ArrayItem<T>>[]): IValidator<T> {
		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			if (!Array.isArray(prop) || prop.length === 0) return {};

			const errors = [];

			for (let index = 0; index < prop.length; index++) {
				const element = prop[index];
				const elementErrors = validators.reduce((acc, validator) => {
					return { ...acc, ...validator(propName, element, translator) };
				}, {});
				if (Object.keys(elementErrors).length > 0) {
					errors.push({ index, errors: elementErrors });
				}
			}

			if (errors.length === 0) return {};

			return { arrayError: errors };
		};
	},
	object: <T extends Record<string, any>>(validators: ValidatorMap<T>) => {
		return (propName: string, prop: T, translator: TranslateService): ValidationErrors => {
			if (prop === null || prop === undefined) {
				return {};
			}

			if (typeof prop !== 'object') {
				return {
					object: translator.instant('modelValidators.errors.object', { propName }),
				};
			}
			const errors = objectValidator.validateObject(prop, validators, translator);
			if (Object.keys(errors).length > 0) {
				return { objectError: errors };
			}
			return {};
		};
	},
};
